<script>
    import { onMount, tick } from 'svelte';
    import ModalOverlay from '$lib/ModalOverlay.svelte';
    import ScrollableSection from '$lib/ScrollableSection.svelte';

    // United from every .js-thumb-img-link on the page on click.
    let carousel_images = [];
    let carousel_slide_els = [];
    let carousel_scroller;

    // Canonical image path (origin-/proxy-agnostic) so dupes across the
    // preview strip and body thumbnails collapse to one slide.
    const image_key = (url) =>
        new URL(url, location.origin).pathname.replace(/^\/img-proxy/, '');

    /** @param {HTMLAnchorElement} clicked_link */
    const open_carousel = async (clicked_link) => {
        const links = /** @type {HTMLAnchorElement[]} */ (
            Array.from(document.querySelectorAll('.js-thumb-img-link'))
        );

        const seen = new Map();
        for (const link of links) {
            const key = image_key(link.href);
            if (seen.has(key)) continue;
            seen.set(key, {
                src: link.href,
                alt: link.querySelector('img')?.alt || ''
            });
        }

        carousel_images = [...seen.values()];
        const start = [...seen.keys()].indexOf(image_key(clicked_link.href));

        carousel_slide_els = [];
        await tick();
        // ModalOverlay reparents itself into #modal-target on mount, which
        // resets scrollLeft. Wait a frame so that move can't undo our scroll.
        requestAnimationFrame(() => {
            carousel_slide_els[start]?.scrollIntoView({
                inline: 'center',
                block: 'nearest',
                behavior: 'instant'
            });
            carousel_scroller?.recalc();
        });
    };

    const close_carousel = () => {
        carousel_images = [];
    };

    // Nearest slide to the viewport centre — stays in sync after swipes/arrows.
    const visible_carousel_index = () => {
        const center_x = window.innerWidth / 2;
        let best = 0;
        let best_dist = Infinity;
        carousel_slide_els.forEach((el, i) => {
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const dist = Math.abs((rect.left + rect.right) / 2 - center_x);
            if (dist < best_dist) {
                best_dist = dist;
                best = i;
            }
        });
        return best;
    };

    const step_carousel = (delta) => {
        const n = carousel_images.length;
        const next = (visible_carousel_index() + delta + n) % n;
        carousel_slide_els[next]?.scrollIntoView({
            inline: 'center',
            block: 'nearest',
            behavior: 'smooth'
        });
    };

    const handle_carousel_keydown = (e) => {
        if (!carousel_images.length) return;
        if (e.key === 'ArrowLeft') step_carousel(-1);
        else if (e.key === 'ArrowRight') step_carousel(1);
    };

    const handle_document_click = (e) => {
        const link =
            e.target instanceof HTMLElement
                ? e.target.closest('.js-thumb-img-link')
                : null;

        if (link instanceof HTMLAnchorElement) {
            e.preventDefault();
            e.stopPropagation();
            open_carousel(link);
        }
    };

    onMount(() => {
        document.addEventListener('click', handle_document_click);
        return () =>
            document.removeEventListener('click', handle_document_click);
    });
</script>

<svelte:window on:keydown={handle_carousel_keydown} />

{#if carousel_images.length}
    <ModalOverlay on:close={close_carousel}>
        <ScrollableSection
            bind:this={carousel_scroller}
            class="w-full h-full"
            content_class="flex h-full snap-x"
        >
            {#each carousel_images as im, i}
                <div
                    bind:this={carousel_slide_els[i]}
                    class="w-full h-full shrink-0 snap-center flex items-center justify-center p-4"
                >
                    <img
                        src={im.src}
                        alt={im.alt}
                        class="max-h-[90dvh] w-auto max-w-[90vw] object-contain"
                        on:load={() => carousel_scroller?.recalc()}
                    />
                </div>
            {/each}
        </ScrollableSection>
    </ModalOverlay>
{/if}
